import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import type { WidgetConfig } from "../shared/types";
import { MESSAGE_TYPES, type QuickPromptPayload } from "../shared/messaging";
import { useWidgetConfig } from "./hooks/useWidgetConfig";
import { useConversation } from "./hooks/useConversation";
import { useConversationMode } from "./hooks/useConversationMode";
import { getParentOrigin } from "./utils/getParentOrigin";
import { WidgetDisplay } from "./components/WidgetDisplay";
import { ApiClient } from "./api/client";

import type { ConversationUIState } from "./types";

function parseInitialConfig(): WidgetConfig | null {
  const params = new URLSearchParams(window.location.search);
  const agentId = params.get("agentId");
  const apiUrl = params.get("apiUrl");
  const sourceUrl = params.get("sourceUrl") || undefined;
  if (!agentId || !apiUrl) return null;
  return { agentId, apiUrl, sourceUrl };
}

function AppInner({
  initialWidgetConfig,
}: {
  initialWidgetConfig: WidgetConfig;
}) {
  const [widgetConfig, setWidgetConfig] =
    useState<WidgetConfig>(initialWidgetConfig);

  const {
    config: agentConfig,
    isLoading: isConfigLoading,
    error: configError,
  } = useWidgetConfig(widgetConfig);

  useEffect(() => {
    if (agentConfig?.organizationId && !widgetConfig.organizationId) {
      setWidgetConfig((prev) => ({
        ...prev,
        organizationId: agentConfig.organizationId,
      }));
    }
  }, [agentConfig, widgetConfig.organizationId]);

  const {
    messages,
    isLoading: isSending,
    statusMessage,
    conversationId,
    pendingApproval,
    sendMessage,
    retryMessage,
    submitApproval,
    skipApproval,
    resetConversation,
  } = useConversation(widgetConfig, agentConfig?.welcomeMessage);

  const apiClient = useMemo(
    () => new ApiClient(widgetConfig.apiUrl, widgetConfig.agentId),
    [widgetConfig.apiUrl, widgetConfig.agentId],
  );

  const handleReset = useCallback(() => {
    resetConversation();
  }, [resetConversation]);

  const {
    voiceInputState,
    isVoiceSupported,
    micPermission,
    playbackState,
    playingMessageId,
    conversationModeEnabled,
    onExitPressed,
    stopAndSubmit,
    onMicPressed,
    onSpeakMessage,
    onUserSent,
  } = useConversationMode({
    apiClient,
    messages,
    isLoading: isSending,
    sendMessage,
  });

  const handleSendMessage = useCallback(
    async (content: string) => {
      onUserSent();
      await sendMessage(content);
    },
    [onUserSent, sendMessage],
  );

  const handleClose = useCallback(() => {
    onExitPressed();

    const parentOrigin = getParentOrigin();
    if (!parentOrigin) return;

    window.parent.postMessage(
      {
        type: MESSAGE_TYPES.CLOSE_WIDGET,
      },
      parentOrigin,
    );
  }, [onExitPressed]);

  const handleCloseRef = useRef(handleClose);
  useEffect(() => {
    handleCloseRef.current = handleClose;
  }, [handleClose]);

  const handleSendMessageRef = useRef(handleSendMessage);
  useEffect(() => {
    handleSendMessageRef.current = handleSendMessage;
  }, [handleSendMessage]);

  const quickPromptReadySentRef = useRef(false);

  useEffect(() => {
    if (window.parent === window) return;

    function handleMessage(event: MessageEvent<unknown>) {
      const parentOrigin = getParentOrigin();
      if (!parentOrigin || event.origin !== parentOrigin) return;

      const { data } = event;
      if (!data || typeof data !== "object") return;

      const message = data as { type?: unknown; payload?: unknown };
      if (message.type !== MESSAGE_TYPES.QUICK_PROMPT) return;

      const payload = message.payload as Partial<QuickPromptPayload>;
      if (
        !payload ||
        typeof payload.prompt !== "string" ||
        payload.prompt.trim().length === 0
      ) {
        return;
      }

      void handleSendMessageRef.current(payload.prompt);
    }

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    if (
      quickPromptReadySentRef.current ||
      window.parent === window ||
      isConfigLoading ||
      configError ||
      !agentConfig
    ) {
      return;
    }

    const parentOrigin = getParentOrigin();
    if (!parentOrigin) return;

    quickPromptReadySentRef.current = true;
    window.parent.postMessage(
      { type: MESSAGE_TYPES.QUICK_PROMPT_READY },
      parentOrigin,
    );
  }, [agentConfig, configError, isConfigLoading]);

  useEffect(() => {
    if (window.parent === window) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        handleCloseRef.current();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const conversationUIState: ConversationUIState = !conversationModeEnabled
    ? "text"
    : voiceInputState === "recording"
      ? "conversation-listening"
      : playbackState === "playing" || playbackState === "loading"
        ? "conversation-speaking"
        : "conversation-processing";

  if (isConfigLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#CC0020] border-r-transparent"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (configError || !agentConfig) {
    return (
      <div className="flex h-full items-center justify-center bg-[#FAFAFA] p-6">
        <div className="max-w-sm text-center" role="alert">
          <svg
            className="mx-auto mb-3 h-12 w-12 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h2 className="mb-2 text-lg font-semibold text-gray-900">
            Unable to Load Chat
          </h2>
          <p className="text-sm text-gray-600">
            {configError ??
              "Failed to load chat configuration. Please try again later."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <WidgetDisplay
      widgetConfig={widgetConfig}
      agentConfig={agentConfig}
      messages={messages}
      isLoading={isSending}
      statusMessage={statusMessage}
      conversationId={conversationId}
      pendingApproval={pendingApproval}
      onSendMessage={handleSendMessage}
      onRetryMessage={retryMessage}
      onApprove={submitApproval}
      onSkip={skipApproval}
      onClose={handleClose}
      voiceEnabled={agentConfig.voiceEnabled && isVoiceSupported}
      onReset={handleReset}
      playbackState={playbackState}
      voiceInputState={voiceInputState}
      isRecording={voiceInputState === "recording"}
      onStartRecording={onMicPressed}
      onStopAndSubmit={stopAndSubmit}
      onSpeakMessage={onSpeakMessage}
      playingMessageId={playingMessageId}
      conversationUIState={conversationUIState}
      onExitConversationMode={onExitPressed}
    />
  );
}

function App() {
  const [config] = useState<WidgetConfig | null>(parseInitialConfig);

  if (!config) {
    return (
      <div className="flex h-full items-center justify-center bg-[#FAFAFA] p-6">
        <div className="max-w-sm text-center" role="alert">
          <h2 className="mb-2 text-lg font-semibold text-gray-900">
            Unable to Load Chat
          </h2>
          <p className="text-sm text-gray-600">
            Missing required configuration parameters.
          </p>
        </div>
      </div>
    );
  }
  return <AppInner initialWidgetConfig={config} />;
}

export default App;
